import { FC, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Drawer, IconButton, Toolbar, Typography, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DateTime } from 'luxon';
import BaseCard from './Subscriptions/BaseCard';
import ConfigurationDrawer from './Subscriptions/ConfigurationDrawer';
import useCompanyId from '../hooks/useCompanyId';
import { useIntegrationsList } from '../stores/FetcherStores/integrationFetcher';
import { useAuth } from '../stores';
import {
  ResellerPrices,
  usePrices,
  useResellerPrices,
} from '../stores/FetcherStores/PricesFetcher';
import { useBillingItemsQuery } from '../stores/FetcherStores/BillingItemFetcher';
import { filterBillingItemsForMonth } from '../utils/billingItem';
import { BillingItemType } from '../model/BillingItem';
import { useGetCompany } from '../stores/FetcherStores/CompanyFetcher';

interface AddSubscriptionDrawerProps {
  open: boolean;
  onClose: () => void;
}

const calculateSubscriptionResellerPrice = (
  resellerPrices: ResellerPrices | null,
  subscriptionId: string,
) => {
  if (!resellerPrices) {
    return null;
  }

  return resellerPrices.subscriptions[subscriptionId].price.applyPercentageDiscount(
    resellerPrices.subscriptions[subscriptionId].discount,
  );
};

const calculateRecurringResellerPrice = (
  resellerPrices: ResellerPrices | null,
  recurringId: string,
) => {
  if (!resellerPrices) {
    return null;
  }

  return resellerPrices.recurring[recurringId].price.applyPercentageDiscount(
    resellerPrices.recurring[recurringId].discount,
  );
};

const AddSubscriptionDrawer: FC<AddSubscriptionDrawerProps> = observer(({ open, onClose }) => {
  const companyId = useCompanyId();
  const auth = useAuth();
  const { data: integrations } = useIntegrationsList(companyId);
  const { data: prices } = usePrices();
  const { data: company } = useGetCompany(companyId);
  const { data: resellerPrices } = useResellerPrices(company?.resellerId || '');
  const { data: billingItems } = useBillingItemsQuery(companyId);
  const activeBillingItems = filterBillingItemsForMonth(
    billingItems || [],
    DateTime.now().toFormat('yyyy-MM'),
  );

  const hasPersonalkollenIntegration = integrations?.some((i) => i.type === 'PERSONALKOLLEN');

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const handleItemSelect = (id: string) => {
    setSelectedItemId(id);
  };

  const handleConfigurationClose = () => {
    setSelectedItemId(null);
  };

  const isSubscriptionActive = (subscriptionId: string) => {
    return (
      activeBillingItems.some((bi) => {
        return (
          bi.type === BillingItemType.SUBSCRIPTION &&
          bi.subscriptionId === subscriptionId &&
          bi.endDate &&
          DateTime.fromISO(bi.endDate) > DateTime.now()
        );
      }) || false
    );
  };

  const getSubscriptionButtonText = (subscriptionId: string, baseText: string) => {
    if (isSubscriptionActive(subscriptionId)) {
      return 'Redan aktiv';
    }
    return baseText;
  };

  const getSubscriptionDescription = (subscriptionId: string, baseDescription: string) => {
    if (subscriptionId === 'RESTAURANT' && !isSubscriptionActive('PLUS')) {
      return `${baseDescription} Plus kommer att aktiveras automatiskt.`;
    }
    return baseDescription;
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 1200,
            height: '100%',
            paddingRight: 4,
            paddingLeft: 4,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              marginTop: 12,
              marginBottom: 2,
              display: 'flex',
              minHeight: 'initial !important',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Typography variant="h4">Funktioner</Typography>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Toolbar>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Box>
              <Grid container spacing={3}>
                <BaseCard
                  title="Plus"
                  price={`${prices?.subscriptions.PLUS.price.format()}/mån`}
                  resellerPrice={
                    resellerPrices
                      ? `${calculateSubscriptionResellerPrice(
                          resellerPrices,
                          'PLUS',
                        )?.format()}/mån`
                      : undefined
                  }
                  description="Ett bra baspaket med kassaregister som enkelt ersätter traditionella kassor."
                  tooltip="I Plus paketet ingår Godkänt kassaregister, Automatisk bokföring med Spiris/Fortnox, Rabatter, Streckkodsläsning med mera."
                  onSelect={() => handleItemSelect('PLUS')}
                  buttonText={getSubscriptionButtonText('PLUS', 'Aktivera Plus')}
                  disabled={isSubscriptionActive('PLUS')}
                />
                <BaseCard
                  title="Restaurang"
                  price={`${prices?.subscriptions.RESTAURANT.price.format()}/mån`}
                  resellerPrice={
                    resellerPrices
                      ? `${calculateSubscriptionResellerPrice(
                          resellerPrices,
                          'RESTAURANT',
                        )?.format()}/mån`
                      : undefined
                  }
                  description={getSubscriptionDescription(
                    'RESTAURANT',
                    'Tilläggspaket som uppgraderar Plus med allt som behövs i restaurangmiljö. Kräver Plus för att aktiveras.',
                  )}
                  tooltip="I Restaurang paketet ingår: Parkera Nota, Bongskrivare, Bordskarta, Dricks, Splitta Nota, Avvikelser med mera."
                  onSelect={() => handleItemSelect('RESTAURANT')}
                  buttonText={getSubscriptionButtonText('RESTAURANT', 'Aktivera Restaurang')}
                  disabled={isSubscriptionActive('RESTAURANT')}
                />
                <BaseCard
                  title="Butik"
                  price={`${prices?.subscriptions.RETAIL.price.format()}/mån`}
                  resellerPrice={
                    resellerPrices
                      ? `${calculateSubscriptionResellerPrice(
                          resellerPrices,
                          'RETAIL',
                        )?.format()}/mån`
                      : undefined
                  }
                  description="Tilläggspaket som uppgraderar Plus med allt som behövs i butikmiljö. Kräver Plus för att aktiveras."
                  tooltip="I Butik paketet ingår: Parkera Nota, Bongskrivare, Bordskarta, Dricks, Splitta Nota, Avvikelser med mera."
                  onSelect={() => handleItemSelect('RETAIL')}
                  buttonText={getSubscriptionButtonText('RETAIL', 'Aktivera Butik')}
                  disabled={isSubscriptionActive('RETAIL')}
                />
                <BaseCard
                  title="Enklaorder"
                  price={`${prices?.subscriptions.ENKLA_ORDER.price.format()}/mån`}
                  resellerPrice={
                    resellerPrices
                      ? `${calculateSubscriptionResellerPrice(
                          resellerPrices,
                          'ENKLA_ORDER',
                        )?.format()}/mån`
                      : undefined
                  }
                  description="Enklaorder är en lösning för att ta beställningar på restaurang och butik."
                  tooltip="Enklaorder är en lösning för att ta beställningar på restaurang och butik."
                  onSelect={() => handleItemSelect('ENKLA_ORDER')}
                  buttonText={getSubscriptionButtonText('ENKLA_ORDER', 'Aktivera Enklaorder')}
                  disabled={isSubscriptionActive('ENKLA_ORDER')}
                />
                <BaseCard
                  title="Användarbehörigheter"
                  price={`${prices?.subscriptions.USER_PERMISSIONS.price.format()}/mån`}
                  resellerPrice={
                    resellerPrices
                      ? `${calculateSubscriptionResellerPrice(
                          resellerPrices,
                          'USER_PERMISSIONS',
                        )?.format()}/mån`
                      : undefined
                  }
                  description="Användarbehörigheter är en lösning för att hantera användarbehörigheter."
                  tooltip="Användarbehörigheter är en lösning för att hantera användarbehörigheter."
                  onSelect={() => handleItemSelect('USER_PERMISSIONS')}
                  buttonText={getSubscriptionButtonText(
                    'USER_PERMISSIONS',
                    'Aktivera Användarbehörigheter',
                  )}
                  disabled={isSubscriptionActive('USER_PERMISSIONS')}
                />
                <BaseCard
                  title="Personalkollen"
                  price={`${prices?.subscriptions.PERSONALKOLLEN.price.format()}/mån`}
                  resellerPrice={
                    resellerPrices
                      ? `${calculateSubscriptionResellerPrice(
                          resellerPrices,
                          'PERSONALKOLLEN',
                        )?.format()}/mån`
                      : undefined
                  }
                  description="Personalkollen är en lösning för att hantera personal och löner."
                  tooltip="Personalkollen är en lösning för att hantera personal och löner."
                  onSelect={() => handleItemSelect('PERSONALKOLLEN')}
                  buttonText={
                    hasPersonalkollenIntegration ? 'Redan aktiv' : 'Aktivera Personalkollen'
                  }
                  disabled={hasPersonalkollenIntegration}
                />
                <BaseCard
                  title="Extern artikelsökning"
                  price={`${prices?.subscriptions.EXTERNAL_SEARCH.price.format()}/mån`}
                  resellerPrice={
                    resellerPrices
                      ? `${calculateSubscriptionResellerPrice(
                          resellerPrices,
                          'EXTERNAL_SEARCH',
                        )?.format()}/mån`
                      : undefined
                  }
                  description="Sök bland tusentals artiklar i ditt kopplade Fortnox. Funkar både med EAN skanning och fritextsökning."
                  onSelect={() => handleItemSelect('EXTERNAL_SEARCH')}
                  buttonText={getSubscriptionButtonText(
                    'EXTERNAL_SEARCH',
                    'Aktivera Extern artikelsökning',
                  )}
                  disabled={isSubscriptionActive('EXTERNAL_SEARCH')}
                />
              </Grid>
            </Box>
            <Typography variant="h4" gutterBottom sx={{ mb: 1, mt: 3 }}>
              Betalmetoder
            </Typography>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 1, mt: 3 }}>
                Kortbetalning
              </Typography>
              <Grid container spacing={3}>
                <BaseCard
                  title="Viva.com"
                  price="Från 0.69%, 58kr/mån"
                  resellerPrice="0.024% - 0.084% profit share"
                  description="Den mest flexibla kortbetalningslösningen som funkar på alla enhter och på webben."
                  onSelect={() => handleItemSelect('VIVA')}
                  buttonText="Lägg till"
                />
                <BaseCard
                  title="Viva.com - terminal"
                  price="Från 0.69%, 58kr/mån"
                  resellerPrice="0.024% - 0.084% profit share"
                  description="Den mest flexibla kortbetalningslösningen som funkar på alla enhter och på webben."
                  onSelect={() => handleItemSelect('VIVA_TERMINAL')}
                  buttonText="Lägg till"
                />
                <BaseCard
                  title="Nets Terminal"
                  price={`${prices?.recurring.NETS_TERMINAL.price.format()}/mån`}
                  resellerPrice={
                    resellerPrices
                      ? `${calculateRecurringResellerPrice(
                          resellerPrices,
                          'NETS_TERMINAL',
                        )?.format()}/mån`
                      : undefined
                  }
                  description="Koppla en Nets Lane 300 eller Move 3600 till kassan."
                  onSelect={() => handleItemSelect('NETS_TERMINAL')}
                  buttonText="Lägg till"
                />
                <BaseCard
                  title="Paytrim + Softpay"
                  price=""
                  resellerPrice=""
                  description=""
                  onSave={() => {}}
                  buttonText="Kommer snart"
                  disabled
                />
                <BaseCard
                  title="Tidypay (Rekommenderas inte)"
                  price={`${prices?.recurring.TIDYPAY_TERMINAL.price.format()}/mån`}
                  resellerPrice={
                    resellerPrices
                      ? `${calculateRecurringResellerPrice(
                          resellerPrices,
                          'TIDYPAY_TERMINAL',
                        )?.format()}/mån`
                      : undefined
                  }
                  description="Starta betalningar via Tidypay."
                  onSelect={() => handleItemSelect('TIDYPAY')}
                  buttonText="Aktivera Tidypay"
                />
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 1, mt: 3 }}>
                    Övriga
                  </Typography>
                </Grid>
                <BaseCard
                  title="Fortnox Faktura"
                  price={`${prices?.subscriptions.FORTNOX_INVOICE.price.format()}/mån`}
                  resellerPrice={
                    resellerPrices
                      ? `${calculateSubscriptionResellerPrice(
                          resellerPrices,
                          'FORTNOX_INVOICE',
                        )?.format()}/mån`
                      : undefined
                  }
                  description="Skapa Fortnox fakturor och ordrar via kassan."
                  onSelect={() => handleItemSelect('FORTNOX_INVOICE')}
                  buttonText={getSubscriptionButtonText(
                    'FORTNOX_INVOICE',
                    'Aktivera Fortnox Faktura',
                  )}
                  disabled={isSubscriptionActive('FORTNOX_INVOICE')}
                />
                <BaseCard
                  title="Swish via Enklakassan"
                  price="1.75 kr/transaktion, 59kr/mån" // EK får 0.236kr/transaktion
                  resellerPrice="0.05kr/transaktion profit share"
                  description="Säkra och billiga swish transaktioner."
                  tooltip="Swish via Enklakassan är en lösning för att ta betalt via Swish."
                  onSelect={() => handleItemSelect('SWISH')}
                  buttonText="Sätt upp"
                />
              </Grid>
            </Box>
            {auth.isAdmin && (
              <>
                <Typography variant="h4" gutterBottom sx={{ mb: 1, mt: 3 }}>
                  Admin alternativ
                </Typography>
                <Box>
                  <Grid container spacing={3}>
                    <BaseCard
                      title="Bokföring"
                      price={`${prices?.subscriptions.BOOKKEEPING.price.format()}/mån`}
                      resellerPrice={
                        resellerPrices
                          ? `${calculateSubscriptionResellerPrice(
                              resellerPrices,
                              'BOOKKEEPING',
                            )?.format()}/mån`
                          : undefined
                      }
                      description=""
                      onSelect={() => handleItemSelect('BOOKKEEPING')}
                      buttonText="Lägg till"
                      disabled={isSubscriptionActive('BOOKKEEPING')}
                    />
                  </Grid>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Drawer>

      <ConfigurationDrawer
        open={!!selectedItemId}
        onClose={handleConfigurationClose}
        selectedItemId={selectedItemId}
      />
    </>
  );
});

export default AddSubscriptionDrawer;
